import { useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import AuthPage from "./pages/auth/Authentication";
import { DarkModeProvider } from "./context/DarkModeContext";
import AppHeader from "./components/header/Header";
import Grammar from "./pages/Grammar";
import Quiz from "./pages/Quiz";
import { auth } from "./firebase/firebase-config";
import {
  onAuthStateChanged,
  User as FirebaseUser,
  signOut,
} from "firebase/auth";
import Imperative from "./pages/individual-topics/Imperativo";
import PresentTense from "./pages/individual-topics/PresenteTense";
import PresentPerfect from "./pages/individual-topics/PreteritoPerfecto";
import PastSimple from "./pages/individual-topics/PreteritoIndefinido";
import PastProgressive from "./pages/individual-topics/PreteritoImperfecto";
import PastPerfect from "./pages/individual-topics/PreteritoPluscuamperfecto";
import Subjuntivo from "./pages/individual-topics/Subjuntivo";
import FuturoImperfecto from "./pages/individual-topics/FuturoImperfecto";
import FuturoPerfecto from "./pages/individual-topics/FuturoPerfecto";
import Possessives from "./pages/individual-topics/Possessives";
import Pronombres from "./pages/individual-topics/Pronombres";
import CondicionalSimple from "./pages/individual-topics/CondicionalSimple";
import Reflexivo from "./pages/individual-topics/Reflexivo";
import VozActivaPasiva from "./pages/individual-topics/VozActivaPasiva";
import Topics from "./pages/Topics";
import "./App.css";
import "./themes/dark-theme.scss";
import "./themes/light-theme.scss";
import ReactGA from "react-ga4";
import { AppPaths } from "./utils/paths";
import { AppUser } from "./utils/AppUser";

// Unified user type (Firebase or Visitor)
const toAppUser = (firebaseUser: FirebaseUser): AppUser => ({
  uid: firebaseUser.uid,
  email: firebaseUser.email,
  displayName: firebaseUser.displayName,
  photoURL: firebaseUser.photoURL,
});

function App() {
  const location = useLocation();
  const [user, setUser] = useState<AppUser>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.pathname !== "/") {
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname,
        title: document.title,
      });
    }
  }, [location]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser ? toAppUser(firebaseUser) : null);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSetUser = (u: AppUser) => setUser(u);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setUser(null);
    }
  };

  if (loading) return <div>Loading...</div>;

  const isAuthenticated = !!user;

  return (
    <DarkModeProvider>
      <Routes>
        <Route
          path="/*"
          element={
            isAuthenticated ? (

              <div id="app">
                <AppHeader handleSignOut={handleSignOut} />
                <Routes>
                  <Route path="/" element={<Grammar />} />
                  <Route path={AppPaths.PRESENT} element={<PresentTense />} />
                  <Route path={AppPaths.PRESENT_PERFECT} element={<PresentPerfect />} />
                  <Route path={AppPaths.PAST_SIMPLE} element={<PastSimple />} />
                  <Route path={AppPaths.PAST_PROGRESSIVE} element={<PastProgressive />} />
                  <Route path={AppPaths.PAST_PERFECT} element={<PastPerfect />} />
                  <Route path={AppPaths.IMPERATIVE} element={<Imperative />} />
                  <Route path={AppPaths.SUBJUNCTIVE} element={<Subjuntivo />} />
                  <Route path={AppPaths.SIMPLE_FUTURE} element={<FuturoImperfecto />} />
                  <Route path={AppPaths.FUTURE_PERFECT} element={<FuturoPerfecto />} />
                  <Route path={AppPaths.POSSESSIVE} element={<Possessives />} />
                  <Route path={AppPaths.PRONOUNS} element={<Pronombres />} />
                  <Route path={AppPaths.CONDITIONAL} element={<CondicionalSimple />} />
                  <Route path={AppPaths.REFLEXIVE} element={<Reflexivo />} />
                  <Route path={AppPaths.VOZ} element={<VozActivaPasiva />} />
                  <Route path={AppPaths.QUIZ} element={<Quiz />} />
                  <Route path={AppPaths.TOPICS} element={<Topics />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            ) : (
              <Navigate to={AppPaths.AUTH} replace />
            )
          }
        />
        <Route
          path={AppPaths.AUTH}
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <AuthPage user={user} setUser={handleSetUser} />
            )
          }
        />
      </Routes>
    </DarkModeProvider>
  );
}

export default App;
